import { Checkbox, Group, Paper, Stack, Switch, Text, UnlabeledField } from '@common/ui'
import TrimmedTextInput from './TrimmedTextInput'
import { com, kotlin } from '@eidu/web'
import UserDropDown = com.eidu.sharedlib.form.definition.UserDropDown
import UserRole = com.eidu.sharedlib.form.definition.UserRole
import EiduUserRole = com.eidu.sharedlib.form.definition.EiduUserRole
import IwUserRole = com.eidu.sharedlib.form.definition.IwUserRole
import KtList = kotlin.collections.KtList
import { withKey } from '../../util/withKey'
import FormKeyField from './FormKeyField'

type UserDropDownItemProps = {
  item: UserDropDown
  onChange?: (item: UserDropDown) => void
}

const UserDropDownItem: React.FC<UserDropDownItemProps> = ({ item, onChange }) => {
  const allRoles = [
    { role: EiduUserRole.Admin, label: 'Admin' },
    { role: EiduUserRole.EcdOfficer, label: 'ECD Officer' },
    { role: EiduUserRole.SubCountyEcdOfficer, label: 'Sub-County ECD Officer' },
    { role: EiduUserRole.EiduCoach, label: 'EIDU Coach' },
    { role: EiduUserRole.EiduOfficer, label: 'EIDU Officer' },
    { role: IwUserRole.FieldOfficer, label: '[IW] Field Officer' },
    { role: IwUserRole.ProgramManager, label: '[IW] Program Manager' },
    { role: IwUserRole.ProjectManager, label: '[IW] Project Manager' },
    { role: IwUserRole.RegionalManager, label: '[IW] Regional Manager' },
    { role: IwUserRole.Supervisor, label: '[IW] Supervisor' },
    { role: IwUserRole.TechnicalSpecialist, label: '[IW] Technical Specialist' },
  ]

  const handleRoleToggle = (toggledRole: UserRole) => {
    const currentRoles = item.roles.asJsReadonlyArrayView()
    const newRoles = currentRoles.includes(toggledRole)
      ? currentRoles.filter((role) => role !== toggledRole)
      : [...currentRoles, toggledRole]

    onChange?.(
      withKey(
        item.copy(
          item.prompt,
          item.key,
          KtList.fromJsArray(newRoles),
          item.required,
          item.unfilledText,
          item.placeholder
        ),
        item
      )
    )
  }

  return (
    <Paper withBorder radius="sm" p="xs">
      <Stack gap="md">
        <Group gap="md" wrap="nowrap" align="flex-start">
          <TrimmedTextInput
            style={{ flex: 1 }}
            label="Prompt"
            value={item.prompt}
            onChange={(e) =>
              onChange?.(
                withKey(
                  item.copy(e.target.value, item.key, item.roles, item.required, item.unfilledText, item.placeholder),
                  item
                )
              )
            }
            disabled={!onChange}
          />
          <FormKeyField
            value={item.key}
            onChange={(e) =>
              onChange?.(
                withKey(
                  item.copy(
                    item.prompt,
                    e.target.value,
                    item.roles,
                    item.required,
                    item.unfilledText,
                    item.placeholder
                  ),
                  item
                )
              )
            }
            disabled={!onChange}
          />
        </Group>
        <Group gap="md" wrap="nowrap" align="flex-start">
          <TrimmedTextInput
            style={{ flex: 1 }}
            label="Unfilled Text"
            value={item.unfilledText ?? ''}
            onChange={(e) =>
              onChange?.(
                withKey(
                  item.copy(item.prompt, item.key, item.roles, item.required, e.target.value, item.placeholder),
                  item
                )
              )
            }
            disabled={!onChange}
          />
          <TrimmedTextInput
            style={{ flex: 1 }}
            label="Placeholder"
            value={item.placeholder ?? ''}
            onChange={(e) =>
              onChange?.(
                withKey(
                  item.copy(item.prompt, item.key, item.roles, item.required, item.unfilledText, e.target.value),
                  item
                )
              )
            }
            disabled={!onChange}
          />
          <UnlabeledField>
            <Switch
              checked={item.required}
              onChange={(e) =>
                onChange?.(
                  withKey(
                    item.copy(item.prompt, item.key, item.roles, e.target.checked, item.unfilledText, item.placeholder),
                    item
                  )
                )
              }
              disabled={!onChange}
              label="Required"
            />
          </UnlabeledField>
        </Group>
        <Stack gap="xs">
          <Text size="sm" fw={600}>
            Allowed Roles
          </Text>
          <Group gap={0}>
            {allRoles.map(({ role, label }) => (
              <Checkbox
                key={label}
                checked={item.roles.asJsReadonlyArrayView().includes(role)}
                onChange={() => handleRoleToggle(role)}
                disabled={!onChange}
                label={label}
                style={{ width: 250 }}
                py="xxs"
              />
            ))}
          </Group>
        </Stack>
      </Stack>
    </Paper>
  )
}

export default UserDropDownItem
